/**
 * groupCollapse.ts
 * Persistence helpers for the Skill Matrix category-group collapse state.
 *
 * Collapsed groups are stored in localStorage as a JSON array of category
 * name strings. The key is shared here so every read/write uses the same slot.
 */

export const GROUP_COLLAPSE_LS_KEY = 'skillMatrix.collapsedGroups';

/**
 * Read collapsed group names from localStorage.
 * Returns an empty array if localStorage is unavailable or the stored value
 * is malformed.
 */
export function readCollapsedGroups(): string[] {
  try {
    const raw = window.localStorage.getItem(GROUP_COLLAPSE_LS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.every((item) => typeof item === 'string')
    ) {
      return parsed as string[];
    }
  } catch {
    // localStorage blocked (private mode) or JSON parse failure — fall back.
  }
  return [];
}

/**
 * Persist the current collapsed group set to localStorage (best-effort).
 * If localStorage is unavailable the call is silently ignored.
 */
export function writeCollapsedGroups(collapsed: Set<string>): void {
  try {
    window.localStorage.setItem(
      GROUP_COLLAPSE_LS_KEY,
      JSON.stringify(Array.from(collapsed)),
    );
  } catch {
    // Ignore write errors.
  }
}
